/*
 * `_useBaseService` is intentionally an internal hook. It composes useApi,
 * useLogger, useAuth, and useCallback, and is only called from other
 * `useXxxService` hooks.
 */
import { useCallback } from 'react';
import { useApi } from '@hooks/services/api';
import { useLogger } from '@providers/loggerProvider';
import { useAuth } from '@providers/authProvider';
import { Cache } from '@utils/cache/cache';
import { ServiceError } from '@types/ServiceError';

export const extractErrorMessage = (error) =>
    error.meta?.error?.message || error.response?.data?.message || error.message || 'Network error occurred';

export const createHandleError = (logger) => (error) => {
    if (logger) logger.error('Operation failed', null, { error });
    return new Error(extractErrorMessage(error));
};

// Cache instances are shared across hook instances but unique per service
const servicesCacheMap = new Map();

/**
 * Base hook providing common service functionality
 * @param {string} serviceName - Name of the service for logging
 * @param {Object} [options] - Hook options
 * @param {string} [options.cacheKey] - Cache key name for this service
 * @param {number} [options.cacheTTL] - Cache TTL in milliseconds
 * @returns {Object} Common service methods and utilities
 */
export const _useBaseService = (serviceName, { cacheKey, cacheTTL } = {}) => {
    const { apiRequest } = useApi();
    const logger = useLogger(serviceName);
    const { user } = useAuth();

    // Get or create the cache instance for this service with user context
    let cacheInstance = null;
    if (cacheKey) {
        const userSpecificCacheKey = user?.id ? `${user.id}_${cacheKey}` : cacheKey;
        
        if (!servicesCacheMap.has(userSpecificCacheKey)) {
            logger.debug('Creating new cache instance with user context', null, { 
                cacheKey: userSpecificCacheKey, 
                cacheTTL, 
                userId: user?.id 
            });
            servicesCacheMap.set(userSpecificCacheKey, new Cache({ 
                keyName: cacheKey, 
                ttl: cacheTTL,
                userId: user?.id 
            }));
        }
        cacheInstance = servicesCacheMap.get(userSpecificCacheKey);
    }

    /**
     * Handles API errors consistently across services
     */
    const handleError = useCallback((error) => {
        const serviceError = ServiceError.fromApiError(error, serviceName);

        // Log the error
        logger.error('Operation failed', null, { error, serviceError });

        return serviceError;
    }, [logger, serviceName]);


    const makeRequest = useCallback(async ({
        endpoint,
        queryParams = {},
        useCache = false
    }) => {
        try {
            const params = { ...queryParams };

            logger.debug('Making request', null, { endpoint, queryParams, cacheKey });
            
            let cachedData = null;
            if (useCache && cacheInstance) {
                logger.debug('Checking cache', null, { cacheKey });
                cachedData = cacheInstance.getAll(); // Get data directly or null
                logger.debug('Cached data retrieved', null, { cachedData: !!cachedData, length: cachedData?.length });
                if (cachedData) {
                    logger.debug('Returning cached data', null, { cacheKey, itemCount: cachedData.length });
                    return cachedData; // Return cached data if found
                }
            }
            
            // Fetch from API if cache miss or caching disabled
            logger.debug('Fetching from API', null, { endpoint, params });
            const apiResponse = await apiRequest(endpoint, 'GET', null, { params }); // Store API response
            logger.debug('API response received', null, { apiResponse });
            
            const responseData = apiResponse.data; // Extract data from API response
            
            // Cache the response data if caching is enabled and data is valid
            if (useCache && cacheInstance && Array.isArray(responseData)) { 
                logger.debug('Caching API response data', null, { cacheKey, itemCount: responseData.length });
                cacheInstance.setAll(responseData); // Cache the actual data array
            }
            
            logger.debug('Returning data from makeRequest', null, { dataLength: responseData?.length });
            return responseData; // Return the data from the API response
        } catch (error) {
            logger.debug('Error in makeRequest', null, { error });
            throw handleError(error, `Failed to fetch ${serviceName} data`);
        }
    }, [apiRequest, handleError, logger, serviceName, cacheInstance, cacheKey]);

    return {
        apiRequest,
        handleError,
        makeRequest
    };
}; 
